import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api } from "../api/client";
import { useData } from "../hooks/useData";
import { I } from "./Icons";
import { clsx } from "./Primitives";
import type { Market, Partner } from "../types";

// ─── Types (mirror server/src/services/newsletter.ts) ───
type Block =
  | { id: string; type: "heading"; text: string }
  | { id: string; type: "text"; text: string }
  | { id: string; type: "story"; title: string; summary: string; take: string; source: string; url: string; country: string }
  | { id: string; type: "image"; url: string; alt: string; link: string; caption: string }
  | { id: string; type: "button"; label: string; url: string }
  | { id: string; type: "quote"; text: string; by: string }
  | { id: string; type: "divider" };
type BlockType = Block["type"];
interface Confidential { block_id: string | null; field: string; kind: "partner" | "deal" | "campaign" | "strategy" | "count"; term: string; excerpt: string }
interface Theme { layout: string; accent: string; background: string }
interface Audience { segment: "subscribers"; interests?: string[]; markets?: string[]; sources?: string[]; exclude?: string[] }
interface AudPerson { email: string; name: string | null; company: string | null; market: string | null; sources: string[]; excluded: boolean }
interface AudCount { count: number; matching: number; excluded: number; total: number; awaiting_confirmation?: number; people?: AudPerson[] }
interface Comment { id: number; block_id: string | null; author: string; author_name: string; body: string; resolved: boolean; created_at: string }
interface Newsletter {
  id: string; kind: "corporate" | "weekly"; name: string; subject: string; preheader: string; blocks: Block[]; theme: Theme;
  audience: Audience; status: string; approved: boolean; approved_at: string | null; approved_by_name?: string | null;
  scheduled_at: string | null; sent_at: string | null; sender_name?: string | null; stats: Record<string, number | string>;
  lint: { words: number; warnings: string[] }; comments: Comment[]; audienceCount: number; period_start?: string | null;
  track?: boolean;
}
interface ListItem {
  id: string; kind: string; name: string; subject: string; status: string; updated_at: string; sent_at: string | null;
  scheduled_at: string | null; stats: Record<string, number>; approved_by_name: string | null; created_by_name: string | null; open_comments: number;
  opened?: number; clicked?: number;
}
interface Stats {
  recipients: number; delivered: number; failed: number; opened: number; unique_clicks: number; clicks: number; unsubscribes: number;
  openRate: number; clickRate: number; unsubscribeRate: number;
  links: { url: string; clicks: number; people: number }[];
  people: { email: string; name: string | null; status: string; first_open: string | null; clicks: number; unsubscribed: boolean }[];
}
interface Suggestion { id: string; block_id: string | null; field: string; original: string; suggestion: string; reason: string; kind: string }
type Doc = Pick<Newsletter, "name" | "subject" | "preheader" | "blocks" | "theme" | "audience"> & { track?: boolean };

const STATUS_LABEL: Record<string, string> = {
  draft: "Draft", in_review: "Waiting for approval", approved: "Approved", scheduled: "Scheduled",
  sending: "Sending…", sent: "Sent", failed: "Failed", cancelled: "Cancelled",
};
const BLOCK_LABEL: Record<BlockType, string> = {
  heading: "Heading", text: "Text", story: "Story", image: "Image", button: "Button", quote: "Quote", divider: "Divider",
};
const LAYOUTS = [["classic", "Classic"], ["editorial", "Editorial"], ["bold", "Bold masthead"]];
const ACCENTS: [string, string, string][] = [["gold", "Saga gold", "#b0873b"], ["navy", "Navy", "#133038"], ["teal", "Teal", "#0E7C66"], ["ocean", "Ocean", "#2D6FA3"], ["ember", "Ember", "#C2410C"]];

const uid = () => "b" + Math.random().toString(36).slice(2, 12);
function emptyBlock(type: BlockType): Block {
  const id = uid();
  switch (type) {
    case "heading": return { id, type, text: "" };
    case "text": return { id, type, text: "" };
    case "story": return { id, type, title: "", summary: "", take: "", source: "", url: "", country: "" };
    case "image": return { id, type, url: "", alt: "", link: "", caption: "" };
    case "button": return { id, type, label: "Read more", url: "https://www.saga-advisory.com" };
    case "quote": return { id, type, text: "", by: "" };
    case "divider": return { id, type };
  }
}
const when = (iso: string | null) => iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
const errMsg = (e: unknown) => (e instanceof Error ? e.message : String(e));

export default function Newsletters({ partners, me, isAdmin, initialId }: {
  partners: Record<string, Partner>; me: string; isAdmin: boolean; initialId?: string | null;
}) {
  const [openId, setOpenId] = useState<string | null>(initialId || null);
  if (openId) return <Editor id={openId} me={me} isAdmin={isAdmin} partners={partners} onClose={() => setOpenId(null)} onOpen={setOpenId} />;
  return <List isAdmin={isAdmin} onOpen={setOpenId} />;
}

// ─── List ───

function List({ isAdmin, onOpen }: { isAdmin: boolean; onOpen: (id: string) => void }) {
  const { data, refetch } = useData<ListItem[]>("/api/newsletters");
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const create = async () => {
    setBusy("new"); setErr(null);
    try { const n = await api.post<Newsletter>("/api/newsletters", {}); onOpen(n.id); }
    catch (e) { setErr(errMsg(e)); } finally { setBusy(null); }
  };
  const weekly = async () => {
    setBusy("weekly"); setErr(null);
    try { const r = await api.post<{ id: string }>("/api/newsletters/weekly/generate", {}); onOpen(r.id); }
    catch (e) { setErr(errMsg(e)); refetch(); } finally { setBusy(null); }
  };
  const remove = async (n: ListItem) => {
    if (!confirm(`Delete "${n.name}"? This can't be undone.`)) return;
    setErr(null);
    try { await api.delete(`/api/newsletters/${n.id}`); refetch(); } catch (e) { setErr(errMsg(e)); }
  };
  const groups: [string, (n: ListItem) => boolean][] = [
    ["Waiting for approval", n => n.status === "in_review"],
    ["Drafts", n => n.status === "draft"],
    ["Approved and scheduled", n => ["approved", "scheduled", "sending"].includes(n.status)],
    ["Sent", n => ["sent", "failed", "cancelled"].includes(n.status)],
  ];
  return (
    <div className="screen nl-screen">
      <div className="page-head">
        <div>
          <h1 className="page-title">Newsletters</h1>
          <div className="page-sub">Corporate newsletters, and the weekly Africa energy briefing drafted from Saga Intelligence for approval</div>
        </div>
        <div className="cd-actions">
          {isAdmin && (
            <button className="btn ghost" disabled={!!busy} onClick={weekly} title="Draft this week's briefing from info.saga-advisory.com">
              {I.radar({ size: 15 })}{busy === "weekly" ? "Drafting from this week's stories…" : "Draft weekly briefing now"}
            </button>
          )}
          <button className="btn primary" disabled={!!busy} onClick={create}>{I.plus({ size: 15 })}New newsletter</button>
        </div>
      </div>
      {err && <div className="set-notice err" style={{ margin: "0 0 14px" }}>{err}</div>}
      {data && data.length === 0 && (
        <div className="card ld-empty">
          <h3>No newsletters yet</h3>
          <p>Start a corporate newsletter, or draft the weekly briefing. A new weekly draft is also made automatically every Friday for your approval.</p>
        </div>
      )}
      {groups.map(([label, test]) => {
        const items = (data || []).filter(test);
        if (!items.length) return null;
        return (
          <div key={label} className="nl-group">
            <div className="section-label nl-group-label">{label}</div>
            <div className="nl-cards">
              {items.map(n => (
                <div key={n.id} className="nl-card card" role="button" tabIndex={0} onClick={() => onOpen(n.id)}
                  onKeyDown={e => e.key === "Enter" && onOpen(n.id)}>
                  <div className="nl-card-top">
                    <span className={clsx("nl-kind", n.kind)}>{n.kind === "weekly" ? "Weekly briefing" : "Corporate"}</span>
                    <span className={clsx("nl-status", "st-" + n.status)}>{STATUS_LABEL[n.status] || n.status}</span>
                  </div>
                  <div className="nl-card-name">{n.name}</div>
                  <div className="nl-card-subject">{n.subject || <em>No subject yet</em>}</div>
                  <div className="nl-card-meta">
                    {n.status === "sent" ? `Sent ${when(n.sent_at)} · ${n.stats?.sent ?? 0} delivered${n.stats?.sent ? ` · ${Math.round(((n.opened || 0) / n.stats.sent) * 100)}% opened · ${Math.round(((n.clicked || 0) / n.stats.sent) * 100)}% clicked` : ""}`
                      : n.status === "scheduled" ? `Scheduled for ${when(n.scheduled_at)}`
                      : `Edited ${when(n.updated_at)}${n.created_by_name ? ` · ${n.created_by_name}` : ""}`}
                    {n.open_comments > 0 && <span className="nl-card-comments">{n.open_comments} comment{n.open_comments > 1 ? "s" : ""}</span>}
                    {n.status !== "sending" && (n.status !== "sent" || isAdmin) && (
                      <button className="icon-btn nl-card-del" title="Delete" aria-label={`Delete ${n.name}`}
                        onClick={e => { e.stopPropagation(); remove(n); }}>{I.trash({ size: 14 })}</button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Editor ───

function Editor({ id, me, isAdmin, partners, onClose, onOpen }: {
  id: string; me: string; isAdmin: boolean; partners: Record<string, Partner>; onClose: () => void; onOpen: (id: string) => void;
}) {
  const { data: loaded, refetch } = useData<Newsletter>(`/api/newsletters/${id}`);
  const { data: meta } = useData<{ aiEnabled: boolean }>("/api/newsletters/meta");
  const [doc, setDoc] = useState<Doc | null>(null);
  const [nl, setNl] = useState<Newsletter | null>(null);
  const [undo, setUndo] = useState<{ doc: Doc; label: string } | null>(null);
  const [saveState, setSaveState] = useState<"saved" | "saving" | "dirty" | "error">("saved");
  const [tab, setTab] = useState<"content" | "comments" | "send">("content");
  const [html, setHtml] = useState("");
  const [lint, setLint] = useState<{ words: number; warnings: string[] }>({ words: 0, warnings: [] });
  const [confidential, setConfidential] = useState<Confidential[]>([]);
  const [mobile, setMobile] = useState(false);
  const [notice, setNotice] = useState<{ kind: "ok" | "err"; text: string } | null>(null);
  const [aiBusy, setAiBusy] = useState<string | null>(null);
  const [briefOpen, setBriefOpen] = useState(false);
  const [suggestions, setSuggestions] = useState<{ summary: string; items: Suggestion[] } | null>(null);
  const [focusBlock, setFocusBlock] = useState<string | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const docRef = useRef<Doc | null>(null);
  docRef.current = doc;

  useEffect(() => {
    if (!loaded) return;
    setNl(loaded);
    setDoc(d => d ?? { name: loaded.name, subject: loaded.subject, preheader: loaded.preheader, blocks: loaded.blocks, theme: loaded.theme, audience: loaded.audience, track: loaded.track !== false });
  }, [loaded]);

  const locked = !!nl && ["sending", "sent"].includes(nl.status);

  // Follow progress while it's going out (or waiting for its scheduled time).
  const live = nl?.status === "sending" || nl?.status === "scheduled";
  useEffect(() => {
    if (!live) return;
    const t = setInterval(refetch, nl?.status === "sending" ? 2000 : 30000);
    return () => clearInterval(t);
  }, [live, nl?.status, refetch]);
  const say = (kind: "ok" | "err", text: string) => { setNotice({ kind, text }); if (kind === "ok") setTimeout(() => setNotice(n => (n?.text === text ? null : n)), 5000); };

  // Live preview
  useEffect(() => {
    if (!doc) return;
    const t = setTimeout(() => {
      api.post<{ html: string; lint: { words: number; warnings: string[] }; confidential?: Confidential[] }>("/api/newsletters/render", { ...doc, firstName: partners[me]?.name?.split(" ")[0] })
        .then(r => { setHtml(r.html); setLint(r.lint); setConfidential(r.confidential || []); }).catch(() => {});
    }, 350);
    return () => clearTimeout(t);
  }, [doc, me, partners]);

  const save = useCallback(async () => {
    const d = docRef.current;
    if (!d) return;
    setSaveState("saving");
    try {
      const r = await api.put<Newsletter & { approvalReset?: boolean }>(`/api/newsletters/${id}`, d);
      setNl(prev => prev ? { ...prev, ...r, comments: prev.comments, audienceCount: prev.audienceCount } : prev);
      setSaveState(docRef.current === d ? "saved" : "dirty");
      if (r.approvalReset) say("err", "Your edit removed the approval — it needs approving again before it can be sent.");
    } catch (e) { setSaveState("error"); say("err", errMsg(e)); }
  }, [id]);

  const change = (next: Doc, undoLabel?: string) => {
    if (locked) return;
    if (undoLabel && doc) setUndo({ doc, label: undoLabel });
    setDoc(next);
    setSaveState("dirty");
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(save, 800);
  };
  const flush = async () => { clearTimeout(saveTimer.current); if (saveState !== "saved") await save(); };
  useEffect(() => () => clearTimeout(saveTimer.current), []);

  const setBlock = (bid: string, patch: Partial<Block>) =>
    doc && change({ ...doc, blocks: doc.blocks.map(b => (b.id === bid ? ({ ...b, ...patch } as Block) : b)) });
  const moveBlock = (bid: string, dir: -1 | 1) => {
    if (!doc) return;
    const i = doc.blocks.findIndex(b => b.id === bid);
    const j = i + dir;
    if (j < 0 || j >= doc.blocks.length) return;
    const blocks = [...doc.blocks];
    [blocks[i], blocks[j]] = [blocks[j], blocks[i]];
    change({ ...doc, blocks });
  };
  const addBlock = (type: BlockType, at: number) => {
    if (!doc) return;
    const blocks = [...doc.blocks];
    const b = emptyBlock(type);
    blocks.splice(at, 0, b);
    change({ ...doc, blocks });
    setFocusBlock(b.id);
  };
  const removeBlock = (bid: string) => doc && change({ ...doc, blocks: doc.blocks.filter(b => b.id !== bid) }, "Delete block");

  // ── AI ──
  const ai = async <T,>(action: string, extra: Record<string, unknown> = {}): Promise<T | null> => {
    if (!doc) return null;
    setAiBusy(action); setNotice(null);
    try { return await api.post<T>(`/api/newsletters/${id}/ai`, { action, ...doc, ...extra }); }
    catch (e) { say("err", errMsg(e)); return null; }
    finally { setAiBusy(null); }
  };
  const aiDraft = async (brief: string) => {
    const r = await ai<{ subject: string; preheader: string; blocks: Block[] }>("draft", { brief });
    if (r && doc) { change({ ...doc, ...r }, "AI draft"); setBriefOpen(false); say("ok", "AI drafted the newsletter — edit anything, or undo."); }
  };
  const aiDesign = async () => {
    const r = await ai<{ theme: Theme; blocks: Block[]; rationale: string }>("design");
    if (r && doc) { change({ ...doc, theme: r.theme, blocks: r.blocks }, "AI design"); say("ok", r.rationale || "AI applied a new design."); }
  };
  const aiCheck = async () => {
    await flush();
    const r = await ai<{ summary: string; suggestions: Suggestion[] }>("check");
    if (r) { setSuggestions({ summary: r.summary, items: r.suggestions }); setTab("content"); }
  };
  const aiRewrite = async (bid: string, instruction: string) => {
    const r = await ai<{ block: Block }>("rewrite", { blockId: bid, instruction });
    if (r && doc) change({ ...doc, blocks: doc.blocks.map(b => (b.id === bid ? r.block : b)) }, "AI rewrite");
  };
  const applySuggestion = (s: Suggestion) => {
    if (!doc) return;
    let next: Doc = doc;
    if (!s.block_id) {
      if (s.field === "subject") next = { ...doc, subject: doc.subject.replace(s.original, s.suggestion) };
      if (s.field === "preheader") next = { ...doc, preheader: doc.preheader.replace(s.original, s.suggestion) };
    } else {
      next = { ...doc, blocks: doc.blocks.map(b => {
        if (b.id !== s.block_id) return b;
        const v = (b as Record<string, unknown>)[s.field];
        return typeof v === "string" ? ({ ...b, [s.field]: v.replace(s.original, s.suggestion) } as Block) : b;
      }) };
    }
    change(next);
    setSuggestions(sg => sg ? { ...sg, items: sg.items.filter(x => x.id !== s.id) } : sg);
  };
  const stillApplies = (s: Suggestion) => {
    if (!doc) return false;
    if (!s.block_id) return (s.field === "subject" ? doc.subject : doc.preheader).includes(s.original);
    const b = doc.blocks.find(x => x.id === s.block_id) as Record<string, unknown> | undefined;
    return !!b && typeof b[s.field] === "string" && (b[s.field] as string).includes(s.original);
  };

  // ── Workflow ──
  const act = async (label: string, fn: () => Promise<unknown>, ok?: string) => {
    setAiBusy(label); setNotice(null);
    try { await flush(); await fn(); await refetch(); if (ok) say("ok", ok); }
    catch (e) { say("err", errMsg(e)); }
    finally { setAiBusy(null); }
  };

  if (!doc || !nl) return <div className="screen nl-screen"><div className="page-sub">Loading…</div></div>;
  const openComments = nl.comments.filter(c => !c.resolved);
  const blockComments = (bid: string) => openComments.filter(c => c.block_id === bid).length;

  return (
    <div className="nl-editor">
      <div className="nl-bar">
        <button className="btn ghost sm" onClick={async () => { await flush(); onClose(); }}>{I.arrowRight({ size: 13, style: { transform: "rotate(180deg)" } })}All newsletters</button>
        <input className="nl-name" value={doc.name} disabled={locked} onChange={e => change({ ...doc, name: e.target.value })} aria-label="Newsletter name" />
        <span className={clsx("nl-status", "st-" + nl.status)}>{STATUS_LABEL[nl.status]}</span>
        <span className="nl-save">{locked ? "" : saveState === "saving" ? "Saving…" : saveState === "dirty" ? "Unsaved" : saveState === "error" ? "Not saved" : "Saved"}</span>
        <span style={{ flex: 1 }} />
        {!locked && (
          <>
            {nl.kind === "corporate" && <button className="btn ghost sm" disabled={!!aiBusy || !meta?.aiEnabled} onClick={() => setBriefOpen(true)}>{I.ai({ size: 14 })}AI draft</button>}
            <button className="btn ghost sm" disabled={!!aiBusy || !meta?.aiEnabled} onClick={aiDesign}>{I.ai({ size: 14 })}{aiBusy === "design" ? "Designing…" : "AI design"}</button>
            <button className="btn ghost sm" disabled={!!aiBusy || !meta?.aiEnabled} onClick={aiCheck}>{I.check({ size: 14 })}{aiBusy === "check" ? "Checking…" : "Check language"}</button>
          </>
        )}
        {undo && !locked && <button className="btn ghost sm" onClick={() => { change(undo.doc); setUndo(null); }}>Undo {undo.label}</button>}
        {nl.status !== "sending" && (nl.status !== "sent" || isAdmin) && (
          <button className="icon-btn nl-del" title="Delete this newsletter" aria-label="Delete this newsletter" onClick={async () => {
            if (!confirm(`Delete "${nl.name}"? This can't be undone.`)) return;
            try { clearTimeout(saveTimer.current); await api.delete(`/api/newsletters/${id}`); onClose(); } catch (e) { say("err", errMsg(e)); }
          }}>{I.trash({ size: 15 })}</button>
        )}
      </div>
      {meta && !meta.aiEnabled && !locked && <div className="set-notice err nl-notice">AI buttons are off: the server has no XAI_API_KEY.</div>}
      {notice && <div className={clsx("set-notice nl-notice", notice.kind)}>{notice.text}</div>}
      {nl.status === "in_review" && isAdmin && !locked && (
        <div className="nl-approval">
          <strong>{nl.kind === "weekly" ? "This week's briefing is waiting for your approval." : "Waiting for approval."}</strong>
          {" "}Edit anything, run <em>Check language</em>, send yourself a test, then approve under <em>Audience &amp; send</em>.
        </div>
      )}

      <div className="nl-split">
        <div className="nl-left">
          <div className="seg nl-tabs">
            <button className={clsx("seg-btn", tab === "content" && "active")} onClick={() => setTab("content")}>Content</button>
            <button className={clsx("seg-btn", tab === "comments" && "active")} onClick={() => setTab("comments")}>
              Comments{openComments.length ? <span className="ld-n">{openComments.length}</span> : null}
            </button>
            <button className={clsx("seg-btn", tab === "send" && "active")} onClick={() => setTab("send")}>Audience &amp; send</button>
          </div>

          {tab === "content" && (
            <>
              {suggestions && (
                <div className="nl-check card">
                  <div className="nl-check-head">
                    <strong>Language check</strong>
                    <span className="nl-check-sum">{suggestions.summary}</span>
                    <button className="btn ghost sm" onClick={() => setSuggestions(null)}>Close</button>
                  </div>
                  {suggestions.items.filter(stillApplies).length === 0
                    ? <div className="set-hint">No suggestions left — it reads well.</div>
                    : (
                      <>
                        {suggestions.items.filter(stillApplies).map(s => (
                          <div key={s.id} className="nl-sugg">
                            <div className="nl-sugg-where">{s.kind} · {s.block_id ? BLOCK_LABEL[doc.blocks.find(b => b.id === s.block_id)?.type || "text"] : s.field}</div>
                            <div className="nl-sugg-diff"><del>{s.original}</del> <ins>{s.suggestion}</ins></div>
                            {s.reason && <div className="set-hint">{s.reason}</div>}
                            <div className="nl-sugg-act">
                              <button className="btn ghost sm" onClick={() => setSuggestions(sg => sg ? { ...sg, items: sg.items.filter(x => x.id !== s.id) } : sg)}>Dismiss</button>
                              <button className="btn primary sm" disabled={locked} onClick={() => applySuggestion(s)}>Accept</button>
                            </div>
                          </div>
                        ))}
                        {!locked && <button className="btn ghost sm" onClick={() => {
                          let d = doc;
                          for (const s of suggestions.items.filter(stillApplies)) {
                            d = !s.block_id
                              ? { ...d, [s.field]: String((d as unknown as Record<string, string>)[s.field] || "").replace(s.original, s.suggestion) }
                              : { ...d, blocks: d.blocks.map(b => b.id !== s.block_id ? b : ({ ...b, [s.field]: String((b as Record<string, unknown>)[s.field] || "").replace(s.original, s.suggestion) } as Block)) };
                          }
                          change(d, "accept all"); setSuggestions(sg => sg ? { ...sg, items: [] } : sg);
                        }}>Accept all</button>}
                      </>
                    )}
                </div>
              )}

              <div className="nl-fields">
                <label className="set-field">
                  <span className="set-label">Subject <span className="nl-count">{doc.subject.length}/60</span></span>
                  <input value={doc.subject} disabled={locked} placeholder="What's in it for the reader — under 60 characters" onChange={e => change({ ...doc, subject: e.target.value })} />
                </label>
                <label className="set-field">
                  <span className="set-label">Preview line</span>
                  <input value={doc.preheader} disabled={locked} placeholder="Shown next to the subject in the inbox" onChange={e => change({ ...doc, preheader: e.target.value })} />
                </label>
                <div className="nl-design">
                  <span className="set-label">Design</span>
                  <select value={doc.theme.layout} disabled={locked} onChange={e => change({ ...doc, theme: { ...doc.theme, layout: e.target.value } })} aria-label="Layout">
                    {LAYOUTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                  <span className="nl-swatches">
                    {ACCENTS.map(([v, l, c]) => (
                      <button key={v} title={l} aria-label={`Accent ${l}`} disabled={locked} className={clsx("nl-swatch", doc.theme.accent === v && "on")} style={{ background: c }}
                        onClick={() => change({ ...doc, theme: { ...doc.theme, accent: v } })} />
                    ))}
                  </span>
                  <select value={doc.theme.background} disabled={locked} onChange={e => change({ ...doc, theme: { ...doc.theme, background: e.target.value } })} aria-label="Background">
                    <option value="sand">Sand background</option>
                    <option value="white">White background</option>
                  </select>
                </div>
              </div>

              {confidential.length > 0 && (
                <div className="nl-conf" role="alert">
                  <div className="nl-conf-h">Not for the public — this can't be approved until it's removed</div>
                  <p>Subscribers include leads, contacts and competitors. Don't name Saga's partners, deals or campaigns, explain Saga's strategy or why something fits a partner or a company Saga works with, or say how many countries or markets Saga covers.</p>
                  <ul>
                    {confidential.map((c, i) => {
                      const idx = c.block_id ? doc.blocks.findIndex(b => b.id === c.block_id) : -1;
                      const where = c.block_id ? `Block ${idx + 1}${c.field !== "text" ? ` · ${c.field}` : ""}` : c.field === "subject" ? "Subject" : "Preview line";
                      return (
                        <li key={i}>
                          <button className="nl-conf-where" onClick={() => c.block_id && setFocusBlock(c.block_id)}>{where}</button>
                          <b>{c.kind === "strategy" || c.kind === "count" ? `“${c.term}”` : c.term}</b>
                          <span className="nl-conf-kind">{c.kind === "partner" ? "Saga partner" : c.kind === "deal" ? "Saga deal" : c.kind === "campaign" ? "Saga campaign" : c.kind === "count" ? "count of countries/markets" : "strategy wording"}</span>
                          <span className="nl-conf-ex">{c.excerpt}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              {!locked && <AddBlock onAdd={t => addBlock(t, 0)} />}
              {doc.blocks.map((b, i) => (
                <div key={b.id}>
                  <BlockCard
                    b={b} index={i} total={doc.blocks.length} locked={locked} focused={focusBlock === b.id}
                    comments={blockComments(b.id)} aiEnabled={!!meta?.aiEnabled} aiBusy={aiBusy === "rewrite"}
                    onChange={p => setBlock(b.id, p)} onMove={d => moveBlock(b.id, d)} onDelete={() => removeBlock(b.id)}
                    onRewrite={ins => aiRewrite(b.id, ins)} onComment={() => { setFocusBlock(b.id); setTab("comments"); }}
                  />
                  {!locked && <AddBlock onAdd={t => addBlock(t, i + 1)} />}
                </div>
              ))}
              <div className="nl-lint">
                {lint.words} words{lint.warnings.length > 0 && <> · {lint.warnings.map(w => <span key={w} className="nl-warn">{w}</span>)}</>}
              </div>
            </>
          )}

          {tab === "comments" && (
            <Comments nl={nl} doc={doc} me={me} isAdmin={isAdmin} focusBlock={focusBlock} setFocusBlock={setFocusBlock} refetch={refetch} />
          )}

          {tab === "send" && (
            <SendPanel nl={nl} doc={doc} locked={locked} isAdmin={isAdmin} busy={aiBusy} change={change} act={act} id={id}
              onDuplicate={async () => { const r = await api.post<{ id: string }>(`/api/newsletters/${id}/duplicate`, {}); onOpen(r.id); }}
              onDelete={async () => {
                if (!confirm(`Delete "${nl.name}"? This can't be undone.`)) return;
                try { await api.delete(`/api/newsletters/${id}`); onClose(); } catch (e) { say("err", errMsg(e)); }
              }} />
          )}
        </div>

        <div className="nl-right">
          <div className="nl-preview-bar">
            <span className="section-label">Preview</span>
            <span className="nl-inbox"><strong>{doc.subject || "No subject"}</strong> — {doc.preheader}</span>
            <div className="seg">
              <button className={clsx("seg-btn", !mobile && "active")} onClick={() => setMobile(false)}>Desktop</button>
              <button className={clsx("seg-btn", mobile && "active")} onClick={() => setMobile(true)}>Phone</button>
            </div>
          </div>
          <div className="nl-frame-wrap">
            <iframe title="Newsletter preview" className={clsx("nl-frame", mobile && "mobile")} srcDoc={html} sandbox="" />
          </div>
        </div>
      </div>

      {briefOpen && <BriefDialog busy={aiBusy === "draft"} onClose={() => setBriefOpen(false)} onRun={aiDraft} hasContent={doc.blocks.some(b => "text" in b && (b as { text: string }).text.trim().length > 30)} />}
    </div>
  );
}

function AddBlock({ onAdd }: { onAdd: (t: BlockType) => void }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={clsx("nl-add", open && "open")}>
      {open ? (
        <div className="nl-add-menu">
          {(Object.keys(BLOCK_LABEL) as BlockType[]).map(t => (
            <button key={t} className="btn ghost sm" onClick={() => { onAdd(t); setOpen(false); }}>{BLOCK_LABEL[t]}</button>
          ))}
          <button className="btn ghost sm" onClick={() => setOpen(false)}>Cancel</button>
        </div>
      ) : <button className="nl-add-btn" onClick={() => setOpen(true)} aria-label="Add a block here">{I.plus({ size: 12, w: 2 })}Add block</button>}
    </div>
  );
}

const QUICK_REWRITES = ["Shorter", "More formal", "Warmer", "Clearer", "Add a call to action"];

function BlockCard({ b, index, total, locked, focused, comments, aiEnabled, aiBusy, onChange, onMove, onDelete, onRewrite, onComment }: {
  b: Block; index: number; total: number; locked: boolean; focused: boolean; comments: number; aiEnabled: boolean; aiBusy: boolean;
  onChange: (p: Partial<Block>) => void; onMove: (d: -1 | 1) => void; onDelete: () => void;
  onRewrite: (instruction: string) => void; onComment: () => void;
}) {
  const [aiOpen, setAiOpen] = useState(false);
  const [instr, setInstr] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { if (focused) ref.current?.scrollIntoView({ behavior: "smooth", block: "center" }); }, [focused]);
  const field = (key: string, label: string, opts: { area?: boolean; rows?: number; placeholder?: string } = {}) => {
    const v = String((b as Record<string, unknown>)[key] ?? "");
    return (
      <label className="set-field">
        <span className="set-label">{label}</span>
        {opts.area
          ? <textarea rows={opts.rows || 4} value={v} disabled={locked} placeholder={opts.placeholder} onChange={e => onChange({ [key]: e.target.value } as Partial<Block>)} />
          : <input value={v} disabled={locked} placeholder={opts.placeholder} onChange={e => onChange({ [key]: e.target.value } as Partial<Block>)} />}
      </label>
    );
  };
  const rewritable = !["divider", "image"].includes(b.type);
  return (
    <div ref={ref} className={clsx("nl-block card", focused && "focused")}>
      <div className="nl-block-head">
        <span className="nl-block-type">{BLOCK_LABEL[b.type]}</span>
        <span style={{ flex: 1 }} />
        <button className="icon-btn" title="Comment on this block" onClick={onComment}>{I.note({ size: 14 })}{comments > 0 && <span className="nl-dot">{comments}</span>}</button>
        {!locked && (
          <>
            {rewritable && aiEnabled && <button className="icon-btn" title="Rewrite with AI" onClick={() => setAiOpen(o => !o)}>{I.ai({ size: 14 })}</button>}
            <button className="icon-btn" title="Move up" disabled={index === 0} onClick={() => onMove(-1)}>{I.chevron({ size: 14, style: { transform: "rotate(-90deg)" } })}</button>
            <button className="icon-btn" title="Move down" disabled={index === total - 1} onClick={() => onMove(1)}>{I.chevron({ size: 14, style: { transform: "rotate(90deg)" } })}</button>
            <button className="icon-btn nl-del" title="Delete block" onClick={onDelete}>{I.trash({ size: 14 })}</button>
          </>
        )}
      </div>
      {aiOpen && !locked && (
        <div className="nl-rewrite">
          {QUICK_REWRITES.map(q => <button key={q} className="btn ghost sm" disabled={aiBusy} onClick={() => { onRewrite(q); setAiOpen(false); }}>{q}</button>)}
          <input placeholder="Or tell the AI what to change…" value={instr} onChange={e => setInstr(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter" && instr.trim()) { onRewrite(instr); setInstr(""); setAiOpen(false); } }} />
        </div>
      )}
      {b.type === "heading" && field("text", "Heading")}
      {b.type === "text" && field("text", "Text", { area: true, rows: 5, placeholder: "Paragraphs separated by a blank line. **bold**, *italic*, [link](https://…), {{first_name|colleague}}" })}
      {b.type === "story" && (
        <div className="set-grid">
          {field("country", "Country")}{field("source", "Source")}
          <div className="wide set-field-wrap">{field("title", "Title")}</div>
          <div className="wide set-field-wrap">{field("summary", "Summary", { area: true, rows: 3 })}</div>
          <div className="wide set-field-wrap">{field("take", "Saga's take", { area: true, rows: 2 })}</div>
          <div className="wide set-field-wrap">{field("url", "Link to the story")}</div>
        </div>
      )}
      {b.type === "image" && (
        <div className="set-grid">
          <div className="wide set-field-wrap">{field("url", "Picture address (https://…)", { placeholder: "Paste the link to a hosted image" })}</div>
          {field("alt", "Description (for screen readers)")}{field("caption", "Caption")}
          <div className="wide set-field-wrap">{field("link", "Link when clicked (optional)")}</div>
        </div>
      )}
      {b.type === "button" && <div className="set-grid">{field("label", "Button text")}{field("url", "Link")}</div>}
      {b.type === "quote" && <div className="set-grid"><div className="wide set-field-wrap">{field("text", "Quote", { area: true, rows: 2 })}</div>{field("by", "Who said it")}</div>}
      {b.type === "divider" && <div className="nl-divider-hint">A thin line between sections</div>}
    </div>
  );
}

function BriefDialog({ busy, hasContent, onClose, onRun }: { busy: boolean; hasContent: boolean; onClose: () => void; onRun: (brief: string) => void }) {
  const [brief, setBrief] = useState("");
  return (
    <div className="fd-scrim" onMouseDown={onClose}>
      <div className="fd-panel" onMouseDown={e => e.stopPropagation()}>
        <div className="fd-head"><div><div className="fd-title">Draft with AI</div>
          <div className="fd-sub">{hasContent ? "The AI rewrites the newsletter from your brief, keeping what's good. You can undo." : "Tell the AI what this issue is about."}</div></div></div>
        <div className="fd-body">
          <label className="set-field wide">
            <span className="set-label">Brief</span>
            <textarea rows={6} autoFocus value={brief} onChange={e => setBrief(e.target.value)}
              placeholder={"e.g. Announce our partnership with NUI (hyperbaric testing, Bergen). Mention we're at Africa Energy Forum in Cape Town in June — invite meetings. Short update on Angola and Namibia."} />
          </label>
          <div className="set-hint" style={{ marginTop: 8 }}>It uses your principals, markets and recent headlines — never pipeline or client deals.</div>
        </div>
        <div className="fd-foot">
          <span style={{ flex: 1 }} />
          <button className="btn ghost sm" onClick={onClose}>Cancel</button>
          <button className="btn primary sm" disabled={busy} onClick={() => onRun(brief)}>{I.ai({ size: 13 })}{busy ? "Writing…" : "Draft newsletter"}</button>
        </div>
      </div>
    </div>
  );
}

function Comments({ nl, doc, me, isAdmin, focusBlock, setFocusBlock, refetch }: {
  nl: Newsletter; doc: Doc; me: string; isAdmin: boolean; focusBlock: string | null; setFocusBlock: (id: string | null) => void; refetch: () => void;
}) {
  const [body, setBody] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const [showResolved, setShowResolved] = useState(false);
  const blockName = (bid: string | null) => {
    if (!bid) return "Whole newsletter";
    const i = doc.blocks.findIndex(b => b.id === bid);
    if (i < 0) return "A removed block";
    const b = doc.blocks[i];
    const snippet = "text" in b ? (b as { text: string }).text : "title" in b ? (b as { title: string }).title : "label" in b ? (b as { label: string }).label : "";
    return `${BLOCK_LABEL[b.type]} ${i + 1}${snippet ? ` — “${snippet.slice(0, 40)}${snippet.length > 40 ? "…" : ""}”` : ""}`;
  };
  const add = async () => {
    setErr(null);
    try { await api.post(`/api/newsletters/${nl.id}/comments`, { body, block_id: focusBlock }); setBody(""); refetch(); }
    catch (e) { setErr(errMsg(e)); }
  };
  const list = nl.comments.filter(c => showResolved || !c.resolved);
  return (
    <div className="nl-comments">
      <div className="card nl-comment-new">
        <div className="set-label">Comment on: <strong>{blockName(focusBlock)}</strong>
          {focusBlock && <button className="ld-link" onClick={() => setFocusBlock(null)}>comment on the whole newsletter instead</button>}
        </div>
        <textarea rows={3} value={body} placeholder="Suggest a change, ask a question…" onChange={e => setBody(e.target.value)} />
        {err && <div className="set-notice err" style={{ margin: "8px 0 0" }}>{err}</div>}
        <div className="ld-notes-foot" style={{ marginTop: 8 }}>
          <label className="set-check"><input type="checkbox" checked={showResolved} onChange={e => setShowResolved(e.target.checked)} /> Show resolved</label>
          <span style={{ flex: 1 }} />
          <button className="btn primary sm" disabled={!body.trim()} onClick={add}>Add comment</button>
        </div>
      </div>
      {list.length === 0 && <div className="set-empty">No comments yet. Click the note icon on a block to comment on that part.</div>}
      {list.map(c => (
        <div key={c.id} className={clsx("nl-comment card", c.resolved && "resolved")}>
          <div className="nl-comment-head">
            <strong>{c.author_name}</strong>
            <button className="ld-link" style={{ marginLeft: 6 }} onClick={() => setFocusBlock(c.block_id)}>{blockName(c.block_id)}</button>
            <span style={{ flex: 1 }} />
            <span className="ld-when">{when(c.created_at)}</span>
          </div>
          <div className="nl-comment-body">{c.body}</div>
          <div className="nl-comment-act">
            <button className="btn ghost sm" onClick={async () => { await api.put(`/api/newsletters/${nl.id}/comments/${c.id}`, { resolved: !c.resolved }); refetch(); }}>
              {c.resolved ? "Reopen" : "Resolve"}
            </button>
            {(c.author === me || isAdmin) && (
              <button className="btn ghost sm set-danger" onClick={async () => { await api.delete(`/api/newsletters/${nl.id}/comments/${c.id}`); refetch(); }}>Delete</button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function SendPanel({ nl, doc, locked, isAdmin, busy, change, act, id, onDuplicate, onDelete }: {
  nl: Newsletter; doc: Doc; locked: boolean; isAdmin: boolean; busy: string | null;
  change: (d: Doc) => void; act: (label: string, fn: () => Promise<unknown>, ok?: string) => Promise<void>; id: string;
  onDuplicate: () => void; onDelete: () => void;
}) {
  const { data: markets } = useData<Market[]>("/api/markets");
  const { data: leadData } = useData<{ sources: { source: string; n: number }[] }>("/api/leads");
  const [count, setCount] = useState<number | null>(nl.audienceCount);
  const [aud2, setAud2] = useState<AudCount | null>(null);
  const [showPeople, setShowPeople] = useState(false);
  const [peopleQ, setPeopleQ] = useState("");
  const [schedule, setSchedule] = useState("");
  const { data: recipients } = useData<{ email: string; name: string | null; status: string; error: string | null }[]>(
    ["sending", "sent", "failed", "cancelled"].includes(nl.status) ? `/api/newsletters/${id}/recipients` : null, [nl.status]);
  const aud = doc.audience || { segment: "subscribers" };
  // Count only people subscribed to this newsletter (weekly or corporate).
  const audKey = JSON.stringify({ ...aud, list: nl.kind });
  useEffect(() => {
    api.post<AudCount>(`/api/newsletters/audience/count${showPeople ? "?list=1" : ""}`, JSON.parse(audKey))
      .then(r => { setCount(r.count); setAud2(r); }).catch(() => {});
  }, [audKey, showPeople]);
  const toggleExclude = (email: string) => {
    const cur = aud.exclude || [];
    change({ ...doc, audience: { ...aud, exclude: cur.includes(email) ? cur.filter(x => x !== email) : [...cur, email] } });
  };
  const toggle = (key: "markets" | "sources", v: string) => {
    const cur = aud[key] || [];
    change({ ...doc, audience: { ...aud, [key]: cur.includes(v) ? cur.filter(x => x !== v) : [...cur, v] } });
  };
  const interests = useMemo(() => (aud.interests || []).join(", "), [aud.interests]);

  return (
    <div className="nl-send">
      <div className="card nl-send-card">
        <div className="section-label">Audience</div>
        <p className="nl-p">Subscribers to {nl.kind === "weekly" ? "the weekly briefing" : "Saga news (corporate)"} who gave consent and haven't unsubscribed or been marked “do not email”. Narrow it down if this issue is for a group:</p>
        <div className="set-label">Markets</div>
        <div className="set-toggles nl-tog">
          {(markets || []).map(m => (
            <button key={m.slug} className={clsx("set-toggle", (aud.markets || []).includes(m.name) && "on")} disabled={locked} onClick={() => toggle("markets", m.name)}>{m.name}</button>
          ))}
        </div>
        <div className="set-label">Signed up via</div>
        <div className="set-toggles nl-tog">
          {(leadData?.sources || []).map(s => (
            <button key={s.source} className={clsx("set-toggle", (aud.sources || []).includes(s.source) && "on")} disabled={locked} onClick={() => toggle("sources", s.source)}>{s.source}</button>
          ))}
        </div>
        <label className="set-field">
          <span className="set-label">Interested in (comma separated, any of)</span>
          <input defaultValue={interests} disabled={locked} placeholder="e.g. LNG, Subsea"
            onBlur={e => change({ ...doc, audience: { ...aud, interests: e.target.value.split(",").map(x => x.trim()).filter(Boolean) } })} />
        </label>
        <div className="nl-count-big">
          <strong>{count ?? "…"}</strong> of {aud2?.total ?? "…"} subscriber{aud2?.total === 1 ? "" : "s"} will receive it
          {aud2 && aud2.excluded > 0 && <span className="nl-excl"> · {aud2.excluded} taken out by hand</span>}
        </div>
        {!!aud2?.awaiting_confirmation && (
          <div className="set-hint">{aud2.awaiting_confirmation} who signed up on info.saga-advisory.com haven't confirmed by email yet — they get it once they do.</div>
        )}
        <button className="ld-link" style={{ marginLeft: 0, alignSelf: "flex-start" }} onClick={() => setShowPeople(v => !v)}>
          {showPeople ? "Hide recipients" : "Show recipients"}
        </button>
        {showPeople && aud2?.people && (
          <div className="nl-people">
            <input className="set-search" placeholder="Search name, email, company…" value={peopleQ} onChange={e => setPeopleQ(e.target.value)} />
            <table>
              <thead><tr><th>Name</th><th>Email</th><th>Company</th><th>Market</th><th /></tr></thead>
              <tbody>
                {aud2.people
                  .filter(p => !peopleQ.trim() || [p.name, p.email, p.company, p.market].join(" ").toLowerCase().includes(peopleQ.trim().toLowerCase()))
                  .map(p => (
                    <tr key={p.email} className={clsx(p.excluded && "excl")}>
                      <td>{p.name || "—"}</td><td>{p.email}</td><td>{p.company || "—"}</td><td>{p.market || "—"}</td>
                      <td style={{ textAlign: "right" }}>
                        {!locked && (
                          <button className={clsx("btn ghost sm", !p.excluded && "set-danger")} onClick={() => toggleExclude(p.email)}>
                            {p.excluded ? "Include again" : "Exclude"}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
            {aud2.people.length === 0 && <div className="set-hint">Nobody matches these filters.</div>}
          </div>
        )}
      </div>

      {["sent", "sending", "failed", "cancelled"].includes(nl.status) && nl.track !== false && <StatsPanel id={id} status={nl.status} />}

      {!locked && (
        <label className="set-check nl-track">
          <input type="checkbox" checked={doc.track !== false} onChange={e => change({ ...doc, track: e.target.checked })} />
          <span>Track opens and clicks — see who read it and which links they used.</span>
        </label>
      )}

      <div className="card nl-send-card">
        <div className="section-label">Check before sending</div>
        <p className="nl-p">Send yourself a test to see it in your own inbox, exactly as subscribers will.</p>
        <button className="btn ghost sm" disabled={!!busy} onClick={() => act("test", () => api.post(`/api/newsletters/${id}/test`, {}), "Test sent to your inbox.")}>
          {I.send({ size: 13 })}{busy === "test" ? "Sending…" : "Send me a test"}
        </button>
      </div>

      <div className="card nl-send-card">
        <div className="section-label">Approval and sending</div>
        {nl.status === "sent" || nl.status === "sending" || nl.status === "failed" || nl.status === "cancelled" ? (
          <>
            <p className="nl-p">
              {nl.status === "sending" ? "Sending now…" : STATUS_LABEL[nl.status]}{nl.sent_at ? ` ${when(nl.sent_at)}` : ""}
              {nl.sender_name ? ` from ${nl.sender_name}'s mailbox` : ""}. {String(nl.stats?.sent ?? 0)} delivered
              {Number(nl.stats?.failed) ? `, ${nl.stats.failed} failed` : ""}{Number(nl.stats?.skipped) ? `, ${nl.stats.skipped} skipped (unsubscribed)` : ""}.
              {nl.stats?.error ? ` ${nl.stats.error}` : ""}
            </p>
            {nl.status === "sending" && isAdmin && <button className="btn ghost sm set-danger" onClick={() => act("cancel", () => api.post(`/api/newsletters/${id}/cancel`, {}), "Sending stopped.")}>Stop sending</button>}
            {recipients && recipients.length > 0 && (
              <div className="nl-recips">
                {recipients.map(r => (
                  <div key={r.email} className="nl-recip"><span>{r.name || r.email}</span><span className={clsx("nl-rs", r.status)}>{r.status}{r.error ? ` — ${r.error}` : ""}</span></div>
                ))}
              </div>
            )}
          </>
        ) : (
          <>
            <p className="nl-p">
              {nl.approved
                ? <>Approved by <strong>{nl.approved_by_name}</strong> {when(nl.approved_at)}. Any edit removes the approval.</>
                : nl.kind === "weekly" ? "The weekly briefing is only sent after an admin approves it." : "An admin approves the final version, then it can be sent."}
            </p>
            {nl.status === "scheduled" ? (
              <>
                <p className="nl-p"><strong>Scheduled for {when(nl.scheduled_at)}.</strong></p>
                {isAdmin && <button className="btn ghost sm" onClick={() => act("cancel", () => api.post(`/api/newsletters/${id}/cancel`, {}), "Schedule cancelled — still approved.")}>Cancel schedule</button>}
              </>
            ) : (
              <div className="nl-send-actions">
                {!isAdmin && nl.status === "draft" && (
                  <button className="btn primary sm" onClick={() => act("submit", () => api.post(`/api/newsletters/${id}/submit`, {}), "Sent for approval — the admins have been emailed.")}>Send for approval</button>
                )}
                {isAdmin && !nl.approved && (
                  <button className="btn primary sm" disabled={!!busy} onClick={() => act("approve", () => api.post(`/api/newsletters/${id}/approve`, {}), "Approved.")}>{I.check({ size: 13 })}Approve this version</button>
                )}
                {isAdmin && nl.approved && (
                  <>
                    <button className="btn primary sm" disabled={!!busy || !count} onClick={() => {
                      if (!confirm(`Send "${doc.subject}" to ${count} recipient${count === 1 ? "" : "s"} now?`)) return;
                      act("send", () => api.post(`/api/newsletters/${id}/send`, {}), "Sending has started.");
                    }}>{I.send({ size: 13 })}Send now to {count}</button>
                    <span className="nl-or">or schedule</span>
                    <input type="datetime-local" value={schedule} onChange={e => setSchedule(e.target.value)} className="ld-select" />
                    <button className="btn ghost sm" disabled={!schedule || !!busy} onClick={() => act("schedule",
                      () => api.post(`/api/newsletters/${id}/send`, { when: new Date(schedule).toISOString() }), "Scheduled.")}>Schedule</button>
                  </>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <div className="nl-send-foot">
        <button className="btn ghost sm" onClick={onDuplicate}>Duplicate</button>
        {(nl.status !== "sent" || isAdmin) && nl.status !== "sending" && <button className="btn ghost sm set-danger" onClick={onDelete}>{I.trash({ size: 13 })}Delete</button>}
      </div>
    </div>
  );
}

function StatsPanel({ id, status }: { id: string; status: string }) {
  const { data: st, refetch } = useData<Stats>(`/api/newsletters/${id}/stats`);
  useEffect(() => { const t = setInterval(refetch, status === "sending" ? 3000 : 60000); return () => clearInterval(t); }, [refetch, status]);
  const [showAll, setShowAll] = useState(false);
  if (!st) return null;
  const tile = (label: string, value: string, sub?: string) => (
    <div className="nl-stat"><div className="nl-stat-v">{value}</div><div className="nl-stat-l">{label}</div>{sub && <div className="nl-stat-s">{sub}</div>}</div>
  );
  const people = showAll ? st.people : st.people.filter(p => p.first_open || p.clicks || p.unsubscribed).slice(0, 12);
  return (
    <div className="card nl-send-card">
      <div className="section-label">Statistics</div>
      <div className="nl-stats">
        {tile("Delivered", String(st.delivered), st.failed ? `${st.failed} failed` : undefined)}
        {tile("Opened", `${st.openRate}%`, `${st.opened} people`)}
        {tile("Clicked", `${st.clickRate}%`, `${st.unique_clicks} people · ${st.clicks} clicks`)}
        {tile("Unsubscribed", String(st.unsubscribes), `${st.unsubscribeRate}%`)}
      </div>
      <p className="set-hint" style={{ margin: 0 }}>Opens are approximate — some mail apps (Apple Mail) load images automatically, others block them. Clicks are exact.</p>
      {st.links.length > 0 && (
        <>
          <div className="set-label" style={{ marginTop: 6 }}>Most clicked links</div>
          {st.links.map(l => (
            <div key={l.url} className="nl-link-row">
              <a href={l.url} target="_blank" rel="noreferrer">{l.url.replace(/^https?:\/\/(www\.)?/, "").slice(0, 70)}</a>
              <span>{l.people} {l.people === 1 ? "person" : "people"} · {l.clicks} click{l.clicks === 1 ? "" : "s"}</span>
            </div>
          ))}
        </>
      )}
      <div className="set-label" style={{ marginTop: 6 }}>Who engaged</div>
      {people.length === 0 && <div className="set-hint">No opens or clicks recorded yet.</div>}
      {people.map(p => (
        <div key={p.email} className="nl-recip">
          <span>{p.name || p.email}</span>
          <span className="nl-rs">
            {p.unsubscribed ? "unsubscribed" : [p.first_open && `opened ${when(p.first_open)}`, p.clicks ? `${p.clicks} click${p.clicks > 1 ? "s" : ""}` : ""].filter(Boolean).join(" · ") || p.status}
          </span>
        </div>
      ))}
      <button className="ld-link" style={{ marginLeft: 0, alignSelf: "flex-start" }} onClick={() => setShowAll(v => !v)}>
        {showAll ? "Show only people who engaged" : `Show all ${st.people.length} recipients`}
      </button>
    </div>
  );
}
